import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Users, Mail, MapPin, Clock, Download,
  RefreshCw, AlertCircle, Search, Filter,
  ChevronRight, TrendingUp, Target, Eye,
  ArrowLeft
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWaitlist = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch stats and entries in parallel
      const [statsRes, entriesRes] = await Promise.all([
        fetch(`${API_URL}/api/waitlist/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/waitlist/entries?limit=100${filterState ? `&state=${filterState}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!statsRes.ok || !entriesRes.ok) {
        throw new Error('Failed to fetch waitlist data');
      }

      const [statsData, entriesData] = await Promise.all([
        statsRes.json(),
        entriesRes.json()
      ]);

      setStats(statsData);
      setEntries(entriesData.entries || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterState]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/waitlist/export`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      
      // Download CSV
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cadence-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export waitlist');
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.firstName?.toLowerCase().includes(search) ||
      entry.lastName?.toLowerCase().includes(search) ||
      entry.email?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStateLabel = (code) => {
    const states = { MD: 'Maryland', DE: 'Delaware', other: 'Other State' };
    return states[code] || code;
  };

  const getConcernLabel = (concern) => {
    const labels = {
      'chronic-condition': 'Chronic Condition',
      'undiagnosed': 'Undiagnosed Symptoms',
      'second-opinion': 'Second Opinion',
      'dismissed': 'Feeling Dismissed',
      'va-navigation': 'VA Navigation',
      'womens-health': "Women's Health",
      'other': 'Other'
    };
    return labels[concern] || concern || 'Not specified';
  };

  const getSourceLabel = (source) => {
    const labels = {
      'social-media': 'Social Media',
      'google': 'Google Search',
      'friend': 'Friend/Family',
      'healthcare-provider': 'Healthcare Provider',
      'other': 'Other'
    };
    return labels[source] || source || 'Not specified';
  };

  if (!isAuthenticated) return null;

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error Loading Waitlist</h2>
          <p>{error}</p>
          <button onClick={fetchData} className="admin-btn primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <div className="admin-nav-links">
              <Link to="/" className="admin-logo-link">
                <img src="/cadence-logo.png" alt="CADENCE" className="admin-logo" />
              </Link>
              <div className="admin-breadcrumb">
                <Link to="/admin" className="admin-back-link">
                  <ArrowLeft size={16} />
                  Dashboard
                </Link>
              </div>
            </div>
            <h1>Waitlist Management</h1>
            <p className="admin-subtitle">
              Pre-launch leads and early access signups
            </p>
          </div>
          <div className="admin-header-actions">
            <button onClick={handleExport} className="admin-btn secondary" data-testid="waitlist-export-btn">
              <Download size={16} />
              Export CSV
            </button>
            <button onClick={fetchData} className="admin-btn secondary">
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card highlight">
            <div className="stat-icon new-intakes">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.total_signups || 0}</span>
              <span className="stat-label">Total Signups</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon pending">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.signups_today || 0}</span>
              <span className="stat-label">Today</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon active">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.signups_this_week || 0}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon messages">
              <MapPin size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{(stats.by_state?.MD || 0) + (stats.by_state?.DE || 0)}</span>
              <span className="stat-label">In Service Area</span>
            </div>
          </div>
        </div>
      )}

      {/* Insights Grid */}
      {stats && (
        <div className="admin-content-grid" style={{ marginBottom: '2rem' }}>
          <div className="admin-card">
            <h3><MapPin size={18} /> By State</h3>
            <div className="insight-list">
              {Object.entries(stats.by_state || {}).map(([state, count]) => (
                <div key={state} className="insight-item">
                  <span className="insight-label">{getStateLabel(state)}</span>
                  <span className="insight-value">{count}</span>
                </div>
              ))}
              {Object.keys(stats.by_state || {}).length === 0 && (
                <p className="empty-text">No data yet</p>
              )}
            </div>
          </div>

          <div className="admin-card">
            <h3><Target size={18} /> By Concern</h3>
            <div className="insight-list">
              {Object.entries(stats.by_concern || {}).slice(0, 5).map(([concern, count]) => (
                <div key={concern} className="insight-item">
                  <span className="insight-label">{getConcernLabel(concern)}</span>
                  <span className="insight-value">{count}</span>
                </div>
              ))}
              {Object.keys(stats.by_concern || {}).length === 0 && (
                <p className="empty-text">No data yet</p>
              )}
            </div>
          </div>

          <div className="admin-card">
            <h3><Eye size={18} /> By Source</h3>
            <div className="insight-list">
              {Object.entries(stats.by_source || {}).slice(0, 5).map(([source, count]) => (
                <div key={source} className="insight-item">
                  <span className="insight-label">{getSourceLabel(source)}</span>
                  <span className="insight-value">{count}</span>
                </div>
              ))}
              {Object.keys(stats.by_source || {}).length === 0 && (
                <p className="empty-text">No data yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="admin-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <Filter size={18} />
          <select 
            value={filterState} 
            onChange={(e) => setFilterState(e.target.value)}
          >
            <option value="">All States</option>
            <option value="MD">Maryland</option>
            <option value="DE">Delaware</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Entries Table */}
      <div className="admin-card">
        <div className="table-container">
          {loading ? (
            <div className="table-loading">
              <RefreshCw className="spin" size={24} />
              <p>Loading entries...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="table-empty">
              <Users size={48} />
              <h3>No Waitlist Entries</h3>
              <p>Share your pre-launch page to start collecting leads!</p>
              <a href="/early-access" target="_blank" rel="noopener noreferrer" className="admin-btn primary">
                View Landing Page
              </a>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>State</th>
                  <th>Concern</th>
                  <th>Source</th>
                  <th>Signed Up</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => (
                  <tr key={entry.id || entry.email}>
                    <td className="name-cell">
                      <span className="primary">{entry.firstName} {entry.lastName}</span>
                      {entry.earlyBird && (
                        <span className="badge success">Early Bird</span>
                      )}
                    </td>
                    <td>
                      <a href={`mailto:${entry.email}`} className="email-link">
                        {entry.email}
                      </a>
                    </td>
                    <td>
                      <span className={`state-badge ${entry.state === 'MD' || entry.state === 'DE' ? 'active' : 'inactive'}`}>
                        {getStateLabel(entry.state)}
                      </span>
                    </td>
                    <td className="concern-cell">
                      {getConcernLabel(entry.primaryConcern)}
                    </td>
                    <td className="source-cell">
                      {getSourceLabel(entry.howDidYouHear)}
                    </td>
                    <td className="date-cell">
                      {formatDate(entry.createdAt)}
                    </td>
                    <td>
                      <button 
                        className="view-btn"
                        onClick={() => setSelectedEntry(selectedEntry?.email === entry.email ? null : entry)}
                        title="View details"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Entry Detail Modal */}
      {selectedEntry && (
        <div className="modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedEntry.firstName} {selectedEntry.lastName}</h2>
              <button className="modal-close" onClick={() => setSelectedEntry(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <a href={`mailto:${selectedEntry.email}`}>{selectedEntry.email}</a>
                </div>
                <div className="detail-item">
                  <label>State</label>
                  <span>{getStateLabel(selectedEntry.state)}</span>
                </div>
                <div className="detail-item">
                  <label>Primary Concern</label>
                  <span>{getConcernLabel(selectedEntry.primaryConcern)}</span>
                </div>
                <div className="detail-item">
                  <label>How They Found Us</label>
                  <span>{getSourceLabel(selectedEntry.howDidYouHear)}</span>
                </div>
                <div className="detail-item">
                  <label>Resources Requested</label>
                  <span>{selectedEntry.selectedResources?.join(', ') || 'None'}</span>
                </div>
                <div className="detail-item">
                  <label>Signed Up</label>
                  <span>{formatDate(selectedEntry.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`badge ${selectedEntry.earlyBird ? 'success' : 'default'}`}>
                    {selectedEntry.earlyBird ? 'Early Bird' : 'Standard'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <a 
                href={`mailto:${selectedEntry.email}?subject=Welcome to CADENCE!`}
                className="admin-btn primary"
              >
                <Mail size={16} />
                Send Email
              </a>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminWaitlist;
